import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

/**
 * Merges class names and mirrors `md:` utilities to `print:` unless a matching
 * `print:` utility for the same property already exists.
 *
 * @param inputs - Class values to merge.
 * @returns Merged class string with print-safe variants.
 */
export function cnWithPrint(...inputs: ClassValue[]) {
    const extractProperty = (className: string): string => {
        const match = className.match(/^([a-z]+(?:-[a-z]+)*)/i);
        return match?.[1] ?? '';
    };

    const baseClassNames = clsx(inputs).split(/\s+/).filter(Boolean);
    const printProperties = new Set<string>();

    baseClassNames.forEach((className) => {
        if (!className.startsWith('print:')) return;
        const property = extractProperty(className.slice(6));
        if (property) {
            printProperties.add(property);
        }
    });

    const withPrintVariants = new Set(baseClassNames);
    baseClassNames.forEach((className) => {
        if (!className.startsWith('md:')) return;

        const suffix = className.slice(3);
        const property = extractProperty(suffix);
        if (property && printProperties.has(property)) return;

        withPrintVariants.add(`print:${suffix}`);
        if (property) {
            printProperties.add(property);
        }
    });

    // Mirror md: responsive classes for print contexts unless already set.
    return twMerge(Array.from(withPrintVariants).join(' '));
}
